import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBagIcon, TrashIcon, MinusIcon, PlusIcon, ArrowLeftIcon, TruckIcon, MapPinIcon, CheckCircleIcon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
interface CartPageProps {
  onNavigate: (page: string) => void;
}
export function CartPage({
  onNavigate
}: CartPageProps) {
  const {
    items,
    updateQuantity,
    removeFromCart,
    clearCart,
    totalPrice,
    totalItems,
    orderType,
    setOrderType
  } = useCart();
  const [orderStatus, setOrderStatus] = useState<'idle' | 'placing' | 'confirmed'>('idle');
  const [confirmedOrder, setConfirmedOrder] = useState<{
    total: string;
    type: string;
    items: number;
  } | null>(null);
  const deliveryFee = orderType === 'delivery' ? 3.99 : 0;
  const taxRate = 0.085;
  const tax = totalPrice * taxRate;
  const finalTotal = totalPrice + deliveryFee + tax;
  const handlePlaceOrder = () => {
    setOrderStatus('placing');
    const orderDetails = {
      total: finalTotal.toFixed(2),
      type: orderType,
      items: totalItems
    };
    setTimeout(() => {
      setConfirmedOrder(orderDetails);
      setOrderStatus('confirmed');
      clearCart();
    }, 2000);
  };
  if (orderStatus === 'confirmed' && confirmedOrder) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="min-h-[70vh] bg-cream flex flex-col items-center justify-center p-4" data-id="element-272">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15,
        delay: 0.2
      }} className="w-28 h-28 bg-green-100 rounded-full flex items-center justify-center mb-8" data-id="element-273">
          <CheckCircleIcon className="w-16 h-16 text-green-600" data-id="element-274" />
        </motion.div>
        <h2 className="font-heading text-4xl md:text-5xl text-charcoal uppercase tracking-wide mb-4 text-center" data-id="element-275">
          Order Confirmed!
        </h2>
        <p className="font-body text-lg text-charcoal/70 mb-2 text-center max-w-md" data-id="element-276">
          Thank you for your order! Your {confirmedOrder.type} order of{' '}
          {confirmedOrder.items} item{confirmedOrder.items > 1 ? 's' : ''} has
          been placed.
        </p>
        <p className="font-heading text-3xl text-victory-red mb-8" data-id="element-277">
          Total: ${confirmedOrder.total}
        </p>
        {confirmedOrder.type === 'delivery' && <div className="bg-white rounded-2xl p-6 shadow-md border border-amber/20 mb-8 text-center max-w-md" data-id="element-278">
            <p className="font-heading text-lg text-charcoal uppercase mb-2" data-id="element-279">
              Estimated Delivery
            </p>
            <p className="font-body text-charcoal/70" data-id="element-280">
              45–60 minutes · DMV metro area
            </p>
            <p className="font-body text-sm text-charcoal/50 mt-2" data-id="element-281">
              We'll call you when your order is on its way.
            </p>
          </div>}
        {(confirmedOrder.type === 'takeaway' || confirmedOrder.type === 'pickup') && <div className="bg-white rounded-2xl p-6 shadow-md border border-amber/20 mb-8 text-center max-w-md" data-id="element-282">
            <p className="font-heading text-lg text-charcoal uppercase mb-2" data-id="element-283">
              Pickup Location
            </p>
            <p className="font-body text-charcoal/70" data-id="element-284">
              7323 Landover Rd, Hyattsville, MD 20785
            </p>
            <p className="font-body text-sm text-charcoal/50 mt-2" data-id="element-285">
              Ready in approximately 30 minutes.
            </p>
          </div>}
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-286">
          <button onClick={() => {
          setOrderStatus('idle');
          setConfirmedOrder(null);
          onNavigate('menu');
        }} className="bg-victory-red hover:bg-red-700 text-white font-heading text-xl px-8 py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider" data-id="element-287">
            Order Again
          </button>
          <button onClick={() => {
          setOrderStatus('idle');
          setConfirmedOrder(null);
          onNavigate('home');
        }} className="bg-charcoal hover:bg-amber text-white font-heading text-xl px-8 py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider" data-id="element-288">
            Back to Home
          </button>
        </div>
      </motion.div>;
  }
  if (items.length === 0) {
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="min-h-[70vh] bg-cream flex flex-col items-center justify-center p-4" data-id="element-289">
        <div className="w-32 h-32 bg-amber/10 rounded-full flex items-center justify-center mb-6" data-id="element-290">
          <ShoppingBagIcon className="w-16 h-16 text-amber" data-id="element-291" />
        </div>
        <h2 className="font-heading text-4xl text-charcoal uppercase tracking-wide mb-4" data-id="element-292">
          Your Cart is Empty
        </h2>
        <p className="font-body text-charcoal/60 mb-8 text-center max-w-md" data-id="element-293">
          Looks like you haven't added anything to your order yet. Let's fix
          that!
        </p>
        <button onClick={() => onNavigate('menu')} className="bg-victory-red hover:bg-red-700 text-white font-heading text-xl px-8 py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider" data-id="element-294">
          Browse Menu
        </button>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-cream py-12" data-id="element-295">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-296">
        <div className="flex items-center space-x-4 mb-8" data-id="element-297">
          <button onClick={() => onNavigate('menu')} className="p-2 rounded-full hover:bg-charcoal/5 transition-colors text-charcoal/60 hover:text-charcoal" data-id="element-298">
            <ArrowLeftIcon className="w-6 h-6" data-id="element-299" />
          </button>
          <h1 className="font-heading text-4xl md:text-5xl text-charcoal uppercase tracking-wide flex items-center" data-id="element-300">
            Your Order{' '}
            <span className="text-amber ml-4 text-2xl bg-amber/10 px-4 py-1 rounded-full" data-id="element-301">
              {totalItems} items
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-302">
          {/* Cart Items List */}
          <div className="lg:col-span-2 space-y-4" data-id="element-303">
            <AnimatePresence data-id="element-304">
              {items.map(item => <motion.div key={item.id} layout initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95,
              height: 0,
              marginBottom: 0
            }} className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-charcoal/5 flex flex-col sm:flex-row items-center gap-4 sm:gap-6" data-id="element-305">
                  <div className="w-20 h-20 bg-cream rounded-xl flex items-center justify-center text-4xl flex-shrink-0 overflow-hidden" data-id="element-306">
                    {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-307" /> : item.emoji}
                  </div>

                  <div className="flex-grow text-center sm:text-left" data-id="element-308">
                    <h3 className="font-heading text-2xl text-charcoal uppercase" data-id="element-309">
                      {item.name}
                    </h3>
                    <p className="font-heading text-xl text-victory-red" data-id="element-310">
                      ${item.price.toFixed(2)}
                    </p>
                  </div>

                  <div className="flex items-center space-x-6 w-full sm:w-auto justify-between sm:justify-end" data-id="element-311">
                    {/* Quantity Controls */}
                    <div className="flex items-center bg-cream rounded-lg border border-charcoal/10" data-id="element-312">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-2 text-charcoal/60 hover:text-victory-red transition-colors" data-id="element-313">
                        <MinusIcon className="w-5 h-5" data-id="element-314" />
                      </button>
                      <span className="font-heading text-xl w-8 text-center" data-id="element-315">
                        {item.quantity}
                      </span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-2 text-charcoal/60 hover:text-green-600 transition-colors" data-id="element-316">
                        <PlusIcon className="w-5 h-5" data-id="element-317" />
                      </button>
                    </div>

                    <div className="text-right hidden sm:block w-24" data-id="element-318">
                      <p className="font-heading text-2xl text-charcoal" data-id="element-319">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>

                    <button onClick={() => removeFromCart(item.id)} className="p-2 text-charcoal/40 hover:text-victory-red transition-colors rounded-full hover:bg-red-50" aria-label="Remove item" data-id="element-320">
                      <TrashIcon className="w-5 h-5" data-id="element-321" />
                    </button>
                  </div>
                </motion.div>)}
            </AnimatePresence>

            <button onClick={() => onNavigate('menu')} className="mt-6 font-heading text-amber hover:text-charcoal uppercase tracking-wider transition-colors flex items-center space-x-2" data-id="element-322">
              <PlusIcon className="w-5 h-5" data-id="element-323" />
              <span data-id="element-324">Add More Items</span>
            </button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1" data-id="element-325">
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-amber/20 sticky top-24" data-id="element-326">
              <h3 className="font-heading text-2xl text-charcoal uppercase tracking-wide mb-6 border-b border-charcoal/10 pb-4" data-id="element-327">
                Order Summary
              </h3>

              {/* Order Type Selector */}
              <div className="mb-6 space-y-2" data-id="element-328">
                <p className="font-heading text-sm uppercase text-charcoal/50 tracking-wider" data-id="element-329">
                  Order Type
                </p>
                <div className="flex gap-2" data-id="element-330">
                  {[{
                  type: 'delivery' as const,
                  icon: <TruckIcon className="w-4 h-4" data-id="element-331" />,
                  label: 'Delivery'
                }, {
                  type: 'takeaway' as const,
                  icon: <ShoppingBagIcon className="w-4 h-4" data-id="element-332" />,
                  label: 'Takeaway'
                }, {
                  type: 'pickup' as const,
                  icon: <MapPinIcon className="w-4 h-4" data-id="element-333" />,
                  label: 'Pickup'
                }].map(({
                  type,
                  icon,
                  label
                }) => <button key={type} onClick={() => setOrderType(type)} className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg font-heading text-xs uppercase tracking-wider transition-all border ${orderType === type ? 'bg-charcoal text-white border-charcoal' : 'bg-cream text-charcoal/60 border-charcoal/10 hover:border-amber'}`} data-id="element-334">
                      {icon}
                      {label}
                    </button>)}
                </div>
                {orderType === 'delivery' && <p className="font-body text-xs text-charcoal/50 mt-1" data-id="element-335">
                    DMV metro area · 12 noon – 8pm
                  </p>}
                {orderType === 'takeaway' && <p className="font-body text-xs text-charcoal/50 mt-1" data-id="element-336">
                    Ready in ~30 mins · 7323 Landover Rd
                  </p>}
                {orderType === 'pickup' && <p className="font-body text-xs text-charcoal/50 mt-1" data-id="element-337">
                    Walk-in · 7323 Landover Rd, Hyattsville
                  </p>}
              </div>

              <div className="space-y-4 font-body text-charcoal/80 mb-6" data-id="element-338">
                <div className="flex justify-between" data-id="element-339">
                  <span data-id="element-340">Subtotal</span>
                  <span className="font-medium" data-id="element-341">${totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between" data-id="element-342">
                  <span data-id="element-343">Delivery Fee</span>
                  <span className="font-medium" data-id="element-344">
                    {deliveryFee > 0 ? `$${deliveryFee.toFixed(2)}` : 'Free'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-345">
                  <span data-id="element-346">Estimated Tax</span>
                  <span className="font-medium" data-id="element-347">${tax.toFixed(2)}</span>
                </div>
              </div>

              <div className="border-t border-charcoal/10 pt-4 mb-8 flex justify-between items-end" data-id="element-348">
                <span className="font-heading text-2xl text-charcoal uppercase" data-id="element-349">
                  Total
                </span>
                <span className="font-heading text-4xl text-victory-red" data-id="element-350">
                  ${finalTotal.toFixed(2)}
                </span>
              </div>

              <button onClick={handlePlaceOrder} disabled={orderStatus === 'placing'} className="w-full bg-victory-red hover:bg-red-700 text-white font-heading text-2xl py-4 rounded-xl shadow-lg transition-colors uppercase tracking-wider disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-351">
                {orderStatus === 'placing' ? <span className="flex items-center justify-center gap-3" data-id="element-352">
                    <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-353">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-354"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-355"></path>
                    </svg>
                    Processing...
                  </span> : orderType === 'delivery' ? 'Place Delivery Order' : orderType === 'takeaway' ? 'Place Takeaway Order' : 'Place Pickup Order'}
              </button>

              <p className="text-center font-body text-xs text-charcoal/40 mt-4" data-id="element-356">
                By placing your order, you agree to our Terms of Service and
                Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}